import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../services/supabase';

export default function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    email: '',
    password: '',
    full_name: '',
    role: '' as 'allocator' | 'manager',
    firm_name: '',
    firm_type: '',
    headquarters: '',
    aum_min: '',
    aum_max: '',
  });

  const update = (field: string, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { data, error: signUpError } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
    });

    if (signUpError || !data.user) {
      setError(signUpError?.message || 'Registration failed');
      setLoading(false);
      return;
    }

    // Create profile
    await supabase.from('profiles').insert({
      id: data.user.id,
      email: form.email,
      full_name: form.full_name,
      role: form.role,
    });

    // Create firm
    await supabase.from('firms').insert({
      name: form.firm_name,
      type: form.firm_type,
      headquarters: form.headquarters,
      aum_min: form.aum_min ? parseFloat(form.aum_min) : null,
      aum_max: form.aum_max ? parseFloat(form.aum_max) : null,
      owner_id: data.user.id,
    });

    navigate('/dashboard');
  };

  const inputStyle = {
    width: '100%', background: '#FFFFFF',
    border: '1px solid #D0D5DD',
    borderRadius: '3px', color: '#0A0A0A',
    fontSize: '14px', padding: '10px 14px', outline: 'none',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  };

  const labelStyle = {
    display: 'block' as const, fontSize: '12px',
    color: '#6B7280', marginBottom: '6px'
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#F5F6F8',
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{ width: '100%', maxWidth: '440px', padding: '0 24px' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '44px', height: '44px', background: '#0A1628',
            borderRadius: '10px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', margin: '0 auto 16px',
            fontSize: '20px', fontWeight: 700, color: '#FFFFFF'
          }}>A</div>
          <h1 style={{ fontFamily: 'Georgia, serif', fontWeight: 400, fontSize: '26px', color: '#0A0A0A', marginBottom: '6px' }}>
            Create your account
          </h1>
          <p style={{ fontSize: '13px', color: '#6B7280' }}>Step {step} of 2</p>
        </div>

        {/* Step indicators */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '28px' }}>
          {[1, 2].map(s => (
            <div key={s} style={{
              flex: 1, height: '3px', borderRadius: '2px',
              background: s <= step ? '#C9A84C' : '#E8EBF0'
            }} />
          ))}
        </div>

        {error && (
          <div style={{
            background: '#FDF6E7', border: '1px solid #D0D5DD',
            borderRadius: '4px', padding: '12px 14px', marginBottom: '16px',
            fontSize: '13px', color: '#C0222A'
          }}>{error}</div>
        )}

        <form onSubmit={step === 1 ? (e) => { e.preventDefault(); setStep(2); } : handleRegister}>
          {step === 1 && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Full name</label>
                <input style={inputStyle} type="text" value={form.full_name}
                  onChange={e => update('full_name', e.target.value)}
                  placeholder="Shiz Azif" required />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Email</label>
                <input style={inputStyle} type="email" value={form.email}
                  onChange={e => update('email', e.target.value)}
                  placeholder="you@firm.com" required />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Password</label>
                <input style={inputStyle} type="password" value={form.password}
                  onChange={e => update('password', e.target.value)}
                  placeholder="••••••••" required minLength={8} />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>I am a...</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {[
                    { value: 'allocator', label: 'Allocator', sub: 'Family office, HNWI, endowment' },
                    { value: 'manager', label: 'Manager', sub: 'Hedge fund, PE, VC, RE, credit' }
                  ].map(option => (
                    <div key={option.value}
                      onClick={() => update('role', option.value)}
                      style={{
                        padding: '14px', borderRadius: '4px', cursor: 'pointer',
                        border: `1px solid ${form.role === option.value ? '#C9A84C' : '#D0D5DD'}`,
                        background: form.role === option.value ? '#FDF6E7' : '#FFFFFF',
                      }}>
                      <div style={{ fontSize: '13.5px', fontWeight: 500, color: form.role === option.value ? '#C9A84C' : '#0A0A0A', marginBottom: '4px' }}>
                        {option.label}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6B7280' }}>{option.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Firm name</label>
                <input style={inputStyle} type="text" value={form.firm_name}
                  onChange={e => update('firm_name', e.target.value)}
                  placeholder="EverestQuant" required />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Firm type</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }}
                  value={form.firm_type} onChange={e => update('firm_type', e.target.value)} required>
                  <option value="">Select type...</option>
                  {form.role === 'allocator' ? <>
                    <option value="family_office">Family Office</option>
                    <option value="hnwi">HNWI</option>
                    <option value="endowment">Endowment</option>
                    <option value="foundation">Foundation</option>
                    <option value="pension">Pension Fund</option>
                  </> : <>
                    <option value="hedge_fund">Hedge Fund</option>
                    <option value="pe">Private Equity</option>
                    <option value="vc">Venture Capital</option>
                    <option value="real_estate">Real Estate</option>
                    <option value="credit">Credit</option>
                    <option value="infrastructure">Infrastructure</option>
                    <option value="multi_strategy">Multi-Strategy</option>
                  </>}
                </select>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Headquarters</label>
                <input style={inputStyle} type="text" value={form.headquarters}
                  onChange={e => update('headquarters', e.target.value)}
                  placeholder="London, United Kingdom" required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' }}>
                <div>
                  <label style={labelStyle}>AUM min ($mm)</label>
                  <input style={inputStyle} type="number" value={form.aum_min}
                    onChange={e => update('aum_min', e.target.value)}
                    placeholder="100" />
                </div>
                <div>
                  <label style={labelStyle}>AUM max ($mm)</label>
                  <input style={inputStyle} type="number" value={form.aum_max}
                    onChange={e => update('aum_max', e.target.value)}
                    placeholder="250" />
                </div>
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: '10px' }}>
            {step === 2 && (
              <button type="button" onClick={() => setStep(1)}
                style={{
                  flex: 1, background: '#FFFFFF',
                  border: '1px solid #D0D5DD',
                  borderRadius: '3px', color: '#3D4552',
                  fontSize: '14px', padding: '11px', cursor: 'pointer',
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}>Back</button>
            )}
            <button type="submit" disabled={loading}
              style={{
                flex: 1, background: '#0A1628', border: 'none',
                borderRadius: '3px', color: '#FFFFFF',
                fontSize: '14px', fontWeight: 600, padding: '11px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                opacity: loading ? 0.7 : 1
              }}>
              {step === 1 ? 'Continue' : loading ? 'Creating account...' : 'Create account'}
            </button>
          </div>
        </form>

        <p style={{ textAlign: 'center', fontSize: '13px', color: '#6B7280', marginTop: '24px' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#C9A84C', textDecoration: 'none' }}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}